/**
 * Canonical cevop commercial offering.
 *
 * Source: the live `cevopApp` marketing website pricing page and
 * `packages/types-core/src/entitlements.ts` `referencePriceNGN` values that page displayed.
 *
 * This file owns commercial facts (plan keys, currency, interval, amounts).
 * Marketing copy, feature bullets, and CTAs stay in `apps/marketing`.
 *
 * Do not invent plans, prices, annual amounts, or USD list prices here.
 * The website footnote "~$3 / ~$9" is marketing approximation, not a BillingPrice.
 * Enterprise displayed "Contact us"; it has no self-serve amount.
 */
export const COMMERCIAL_OFFERING_SOURCE = {
    website: 'cevopApp/apps/marketing/src/pages/Pricing.tsx',
    entitlements: 'cevopApp/packages/types-core/src/entitlements.ts',
    extractedOn: '2026-08-16',
};
/**
 * Catalog products that exist as implemented cevop capabilities.
 */
export const COMMERCIAL_CATALOG_PRODUCTS = [
    {
        key: 'platform',
        name: 'cevop Platform',
        description: 'Organization access to cevop identity, tenancy, and the business workspace.',
        kind: 'PLATFORM',
        capabilityKey: 'platform',
        released: true,
    },
    {
        key: 'sales',
        name: 'Sales / POS',
        description: 'Counter sales and POS sessions.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'sales',
        released: true,
    },
    {
        key: 'inventory',
        name: 'Inventory',
        description: 'Cost, stock levels, and movements.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'inventory',
        released: true,
    },
    {
        key: 'accounting',
        name: 'Accounting',
        description: 'Books and reports. Website name: Finance.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'accounting',
        released: true,
    },
    {
        key: 'procurement',
        name: 'Procurement',
        description: 'Suppliers, purchase orders, and receiving.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'procurement',
        released: true,
    },
    {
        key: 'workforce',
        name: 'Workforce',
        description: 'People, attendance, leave, payroll, and terminals.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'workforce',
        released: false,
    },
    {
        key: 'crm',
        name: 'CRM',
        description: 'Contacts, companies, leads, opportunities, and pipelines.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'crm',
        released: false,
    },
    {
        key: 'comms',
        name: 'Comms',
        description: 'Native messaging, groups, channels, walkie-talkie, and calls.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'comms',
        released: false,
    },
    {
        key: 'mail',
        name: 'Mail',
        description: 'Business email: mailboxes, domains, delivery, and mail administration.',
        kind: 'BUSINESS_PRODUCT',
        capabilityKey: 'mail',
        released: false,
    },
];
const NGN_MONTH = {
    currencyCode: 'NGN',
    interval: 'MONTH',
};
/** Website paid/suite packaging. Free does not force these products. */
export const SUITE_PLAN_PRODUCT_KEYS = ['platform', 'sales', 'inventory', 'accounting'];
/** Free plan: platform only. Comms and Mail are independently activated. */
export const FREE_PLAN_PRODUCT_KEYS = ['platform'];
/** @deprecated Use SUITE_PLAN_PRODUCT_KEYS. Kept for catalog helpers that mean the paid bundle. */
export const COMMERCIAL_INCLUDED_PRODUCT_KEYS = SUITE_PLAN_PRODUCT_KEYS;
/** Implemented products that are not auto-granted with Free. Paid plans may still bundle Sales/Inventory/Accounting. */
export const INDEPENDENT_CATALOG_PRODUCT_KEYS = ['procurement', 'workforce', 'crm', 'comms', 'mail'];
/** Products a customer may choose at signup or activate later. Not platform. Includes unreleased catalog keys for future activation. */
export const STARTABLE_CATALOG_PRODUCT_KEYS = [
    'sales',
    'inventory',
    'accounting',
    'procurement',
    'workforce',
    'crm',
    'comms',
    'mail',
];
/**
 * Shipping Business / POS suite apps that every organization may open without a Settings toggle.
 * Unreleased products (CRM, Comms, Mail, Workforce) are catalogued but not always-on —
 * they support independent activate/deactivate once released.
 * See ADR 0097 (open suite for released apps) and release metadata below.
 */
export const ALWAYS_ON_CATALOG_PRODUCT_KEYS = [
    'sales',
    'inventory',
    'accounting',
    'procurement',
];
/** Capability keys considered released for shipping chrome / self-serve. */
export const RELEASED_CATALOG_PRODUCT_KEYS = COMMERCIAL_CATALOG_PRODUCTS.filter((product) => product.released !== false).map((product) => product.capabilityKey);
export function isReleasedProduct(capabilityKey) {
    const product = COMMERCIAL_CATALOG_PRODUCTS.find((row) => row.capabilityKey === capabilityKey || row.key === capabilityKey);
    if (!product) {
        return false;
    }
    return product.released !== false;
}
export function isAlwaysOnCatalogProduct(capabilityKey) {
    return ALWAYS_ON_CATALOG_PRODUCT_KEYS.includes(capabilityKey);
}
export function isStartableCatalogProduct(capabilityKey) {
    return STARTABLE_CATALOG_PRODUCT_KEYS.includes(capabilityKey);
}
export function parseStartableProductKeys(keys) {
    if (!Array.isArray(keys)) {
        return [];
    }
    const seen = new Set();
    const parsed = [];
    for (const key of keys) {
        if (typeof key !== 'string' || !isStartableCatalogProduct(key) || seen.has(key)) {
            continue;
        }
        seen.add(key);
        parsed.push(key);
    }
    return parsed;
}
export const COMMERCIAL_PLANS = [
    {
        key: 'free',
        name: 'Free',
        description: 'No charge. Core tools for getting started.',
        productKeys: FREE_PLAN_PRODUCT_KEYS,
        prices: [
            {
                key: 'free-ngn-month',
                ...NGN_MONTH,
                amount: '0.00',
                nickname: 'Free monthly',
            },
        ],
        contactSales: false,
    },
    {
        key: 'starter',
        name: 'Starter',
        description: '₦4,500 per month. Full suite for a single business.',
        productKeys: SUITE_PLAN_PRODUCT_KEYS,
        prices: [
            {
                key: 'starter-ngn-month',
                ...NGN_MONTH,
                amount: '4500.00',
                nickname: 'Starter monthly',
            },
        ],
        contactSales: false,
    },
    {
        key: 'growth',
        name: 'Growth',
        description: '₦18,000 per month. Full suite for growing teams.',
        productKeys: SUITE_PLAN_PRODUCT_KEYS,
        prices: [
            {
                key: 'growth-ngn-month',
                ...NGN_MONTH,
                amount: '18000.00',
                nickname: 'Growth monthly',
            },
        ],
        contactSales: false,
    },
    {
        key: 'enterprise',
        name: 'Enterprise',
        description: 'Custom pricing. Assigned after a sales conversation.',
        productKeys: SUITE_PLAN_PRODUCT_KEYS,
        prices: [],
        contactSales: true,
    },
];
/**
 * Operator-only. Not a website list price and not shown on tenant self-serve catalog.
 * Admins use this to grant Enterprise after a sales conversation.
 */
export const OPERATOR_ASSIGNABLE_PRICES = [
    {
        planKey: 'enterprise',
        key: 'enterprise-operator-month',
        currencyCode: 'NGN',
        interval: 'MONTH',
        amount: '0.00',
        nickname: 'Enterprise (operator assigned)',
    },
];
export const COMMERCIAL_PLAN_ORDER = COMMERCIAL_PLANS.map((plan) => plan.key);
export function commercialPlanByKey(key) {
    return COMMERCIAL_PLANS.find((plan) => plan.key === key);
}
export function commercialProductByKey(key) {
    return COMMERCIAL_CATALOG_PRODUCTS.find((product) => product.key === key);
}
//# sourceMappingURL=commercialOffering.js.map